import React, { useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, Sparkles, Gift } from 'lucide-react';
import { triggerCelebration } from '@/lib/confetti';
interface CelebrationDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'newsletter' | 'strategy-call' | 'contact';
  projectedSavings?: string;
}
export const CelebrationDialog: React.FC<CelebrationDialogProps> = ({
  open,
  onClose,
  type,
  projectedSavings
}) => {
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (open && !hasFiredRef.current) {
      // Fire confetti immediately when dialog opens
      triggerCelebration();
      hasFiredRef.current = true;
    }
    if (!open) {
      hasFiredRef.current = false;
    }
  }, [open]);

  const handleCelebrate = () => {
    triggerCelebration();
    setTimeout(onClose, 1500);
  };
  const getContent = () => {
    switch (type) {
      case 'newsletter':
        return {
          title: "Welcome to the Contencraft Family! 🎉",
          message: "You're now part of an exclusive community of content leaders transforming their businesses.",
          icon: <Gift className="w-16 h-16 text-primary mx-auto mb-4" />,
          buttonText: "Let's Celebrate! 🎊"
        };
      case 'strategy-call':
        return {
          title: "Your Spot is Secured! 🚀",
          message: projectedSavings ? `Amazing! You could save ${projectedSavings} over 5 years. Our team will contact you within 24 hours to schedule your exclusive strategy call.` : "Our team will contact you within 24 hours to schedule your exclusive strategy call and unlock your content transformation.",
          icon: <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />,
          buttonText: "Celebrate My Success! 🎉"
        };
      case 'contact':
        return {
          title: "Message Received! ✨",
          message: "Thank you for reaching out! Our content strategists will get back to you within 24 hours with insights tailored to your business.",
          icon: <Sparkles className="w-16 h-16 text-primary mx-auto mb-4" />,
          buttonText: "Awesome! 🎊"
        };
      default:
        return {
          title: "Success! 🎉",
          message: "Thank you for your submission!",
          icon: <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />,
          buttonText: "Celebrate! 🎊"
        };
    }
  };
  const content = getContent();
  return <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-background to-muted border-2 border-primary/20">
        <DialogHeader className="text-center space-y-4">
          {content.icon}
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent mx-0 my-0 px-0 text-center">
            {content.title}
          </DialogTitle>
          <p className="text-muted-foreground leading-relaxed text-center">
            {content.message}
          </p>
        </DialogHeader>
        
        <div className="flex justify-center pt-4">
          <Button onClick={handleCelebrate} className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            {content.buttonText}
          </Button>
        </div>
        
        <div className="text-center text-xs text-muted-foreground mt-4 opacity-75">
          Building world-class content experiences
        </div>
      </DialogContent>
    </Dialog>;
};